/**
 * Admin CV bank API service.
 *
 * Every candidate's CV in one filterable list, plus any one of them in full. Admin-only:
 * the server checks the caller's phone against an active ADMIN row in `users` (the same
 * check behind the `admin` flag on /diil/employer/me) and answers 403 otherwise, so a
 * non-admin who reaches these functions gets `{ success: false, status: 403 }` rather than
 * data.
 *
 * Same `{ success, data, status }` envelope as candidate.js and cv.js.
 */

import http from '../client';
import { ENDPOINTS } from '../config';

const handleApiSuccess = (response) => ({
  success: true,
  data: response.data?.data || response.data,
  status: response.status,
  message: response.data?.message || 'Success'
});

const handleApiError = (error) => ({
  success: false,
  error: error.message || 'An error occurred',
  status: error.status || 500,
  requiresAuth: error.status === 401,
  forbidden: error.status === 403
});

/**
 * Drop keys the server should read as "not asked for".
 *
 * The client's buildUrl already skips null/undefined but keeps empty strings, and an empty
 * string is not the same question: `?skill=` would reach a filter that means "skills
 * starting with nothing". Cleared inputs have to disappear from the URL entirely.
 */
const compact = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );

/**
 * One page of the CV bank.
 *
 * @param {Object} params - any of: q (free text over everything the CV says — name, phone,
 *   email, summary, skills, work history, education and every other section; words are
 *   ANDed), skill, jobTitle, education, gender, ageMin, ageMax, sector (one field of
 *   work — see src/data/cvSectors.js for the keys; the server expands it to the words that
 *   field is written in and matches the work history, skills, desired position and the
 *   candidate's own chosen sector),
 *   location, minCompleteness, hasWorkExperience, hasFile, language, cvSource,
 *   registeredFrom, registeredTo (YYYY-MM-DD), sort, page (0-based), size
 * @returns {Promise<Object>} { success, data: Page<row>, status } — `data` carries
 *   `content`, `totalElements`, `totalPages`, `last`, the standard Spring page shape the
 *   jobs list already reads.
 */
export const listAdminCvs = async (params = {}) => {
  try {
    const response = await http.get(ENDPOINTS.ADMIN_CV.LIST, compact(params));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * The CV-bank list rows for a named set of people, in the order asked for.
 *
 * For a screen that already knows who it is showing — so it can render CvRowCard rather
 * than a thinner row of its own. Anyone without an active CV is absent from the answer.
 *
 * Admin-only, like everything else in this file. An applicants list is NOT the caller for
 * it: "Миний зарууд" used this and every ordinary employer got a 403 and a bare
 * name-and-phone card. That screen now calls `getApplicantCvRows` in
 * services/employerJobs.js, which returns these same rows through the ad the people
 * applied to — authorized by owning the posting rather than by being an admin.
 *
 * @param {Array<number|string>} candidateIds
 * @returns {Promise<Object>} { success, data: Array<row>, status }
 */
export const getAdminCvRowsByCandidates = async (candidateIds = []) => {
  try {
    const response = await http.get(ENDPOINTS.ADMIN_CV.BY_CANDIDATES, { candidateIds });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * One candidate's full CV — all eleven sections plus their phone and email.
 *
 * Shaped exactly like the employer applicant-CV response, so `toCvView` in
 * services/employerJobs.js maps it for `ProfileDetails` with no extra work.
 *
 * @param {number|string} candidateId
 */
export const getAdminCv = async (candidateId) => {
  try {
    const response = await http.get(ENDPOINTS.ADMIN_CV.DETAIL(candidateId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Ask the AI to search the CV bank directly: read what kind of person the admin described
 * ("ярианы ур чадвартай, англи хэлтэй, оюутан биш") and return real candidates it judged as
 * genuinely fitting, ranked, each with a one-line reason — not a translation into
 * listAdminCvs' filter fields. That approach was tried first and dropped: a structured
 * "language" filter is the CV document's language, not whether the candidate speaks one, and
 * there is no way to represent a negative requirement ("оюутан биш") as a positive
 * literal-match filter without it backfiring.
 *
 * Stateless on the server — `turns` is the running conversation for this search session,
 * resent whole on every call so a follow-up like "надад илүү туршлагатай хүн хэрэгтэй" can
 * refine the previous query. This should only ever be triggered by an explicit submit, never
 * per keystroke.
 *
 * The timeout is long because a single call is up to four sequential Gemini calls: a hint
 * pass, the ranking pass, a retry of the ranking pass against a smaller candidate pool if the
 * first answer overran its output budget, and an optional pass that re-grounds each reason in
 * the candidate's real interview. It was 35s, which the retry could exceed on its own — and
 * an abort here costs the whole answer, since the catch below falls back to a plain keyword
 * search. The server skips the last pass when it is already running late, so this ceiling
 * should not be reached in practice.
 *
 * @param {string} text - what the admin just typed
 * @param {Array<{role: 'user'|'model', text: string}>} [turns] - prior turns this session
 * @returns {Promise<Object>} { success, data: { analysis, noMatch, picks: [{ row, reason }] },
 *   status } — `row` is the same shape listAdminCvs returns per candidate, ready to render
 *   with CvRowCard; caller must fall back to a plain `q` search on failure.
 */
export const aiSearchCvs = async (text, turns = []) => {
  try {
    const response = await http.post(
      ENDPOINTS.ADMIN_CV.AI_SEARCH,
      { text, turns },
      { timeout: 60000 }
    );
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Put a candidate's CV on the CV bank's shortlist, or take it off again.
 *
 * The shortlist is shared by every admin rather than kept per person, so what comes back is
 * the CV's new state for everyone — including `favoritedBy`, the name to show on the card.
 * Toggling rather than save/unsave because the heart is one control and a second click has
 * to undo the first.
 *
 * @param {number|string} candidateId
 * @returns {Promise<Object>} { success, data: { candidateId, favorited, favoritedBy,
 *   favoritedAt }, status }
 */
export const toggleCvFavorite = async (candidateId) => {
  try {
    const response = await http.post(ENDPOINTS.ADMIN_CV.FAVORITE(candidateId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

const adminCvService = {
  listAdminCvs,
  getAdminCvRowsByCandidates,
  getAdminCv,
  aiSearchCvs,
  toggleCvFavorite
};

export default adminCvService;
